import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ensureWorkspace } from '../lib/workspaceBoot';

// Product catalog. Every Tailwind color token is a complete literal here so the
// compiler still emits it (no dynamically-assembled class fragments). Colors are
// unchanged from the original per-product palette — only the layout was reworked.
const PLATFORMS = [
  {
    id: 'pcg', code: 'PCG', name: 'Precision Compliance Group',
    desc: 'Medicare Advantage compliance operations, audit management, and regulatory intelligence.',
    pills: ['PSI', 'CAP', 'FDR', 'Scrubber', 'PPM'],
    iconPath: 'M9 12.75L11.25 15 15 9.75m-3-7.036A11.959 11.959 0 013.598 6 11.99 11.99 0 003 9.749c0 5.592 3.824 10.29 9 11.623 5.176-1.332 9-6.03 9-11.622 0-1.31-.21-2.571-.598-3.751h-.152c-3.196 0-6.1-1.248-8.25-3.285z',
    iconGrad: 'from-orange-500 to-amber-500', iconShadow: 'shadow-blue-500/25 group-hover:shadow-blue-500/40',
    tint: 'from-orange-50 to-amber-50/60', hoverBorder: 'group-hover:border-blue-300',
    hoverShadow: 'group-hover:shadow-[0_8px_30px_-8px_rgba(59,130,246,0.25)]', ring: 'group-focus-visible:ring-blue-400',
    subtitle: 'text-blue-700', pill: 'bg-blue-50 text-blue-700 border-blue-200/70', arrow: 'text-blue-600',
  },
  // PMRA and PNA removed from the picker 2026-07-07: PSI (PCG) is the only
  // product users land on. Their /pmra and /pna routes still exist for direct
  // access, and Network Adequacy also lives as a module inside PSI.
];

export default function PlatformSelector() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    fetch(`${process.env.REACT_APP_API_URL}/api/auth/session`, {
      method: 'GET',
      credentials: 'include'
    })
      .then(res => res.json())
      .then(data => {
        if (data.success) {
          setUser(data.user);
          localStorage.setItem('sessionUser', JSON.stringify(data.user));
          // TPA staff with more than one workspace choose (or reopen) one first.
          if (ensureWorkspace(data.user, navigate)) return;
          // Platform superuser default landing: skip the product picker and
          // drop straight onto the Platform Admin Console — unless they are
          // currently impersonating a client, in which case let them fall
          // through to the normal product picker / app routes so they can
          // act on the impersonated org's data.
          const isPlatformSuperuser =
            data.user.user_type === 'platform_superuser' ||
            data.user.effective_user_type === 'platform_superuser' ||
            data.user.role === 'superadmin';
          // /api/auth/session returns impersonation as a TOP-LEVEL field, not nested under user.
          const isImpersonating = !!data.impersonation && data.impersonation.active !== false;
          if (isPlatformSuperuser && !isImpersonating) {
            navigate('/platform-admin', { replace: true });
            return;
          }
          // 2026-08-24 role redesign: the org super user's home is People
          // (access management) — they no longer see the compliance workspace.
          const selUt = data.user.user_type || data.user.effective_user_type || null;
          const isAccessAdmin = selUt ? selUt === 'org_superadmin' : data.user.role === 'superuser';
          if (isAccessAdmin) {
            navigate('/people', { replace: true });
            return;
          }
          // PSI (PCG) is the only product in the picker now, so send everyone
          // else straight to it — login lands directly on PSI, no picker step.
          navigate('/app', { replace: true });
          return;
        } else {
          navigate('/login');
        }
        setLoading(false);
      })
      .catch(() => navigate('/login'));
  }, [navigate]);

  const handleSelect = async (platform) => {
    localStorage.setItem('lastPlatform', platform);
    if (platform === 'pcg') {
      navigate('/app');
    } else if (platform === 'pmra') {
      navigate('/pmra');
    } else if (platform === 'pna') {
      navigate('/pna');
    }
  };

  const handleLogout = () => {
    fetch(`${process.env.REACT_APP_API_URL}/api/auth/logout`, {
      method: 'GET',
      credentials: 'include'
    }).then(() => {
      localStorage.removeItem('sessionUser');
      localStorage.removeItem('lastPlatform');
      window.location.href = '/login';
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen bg-[#f8fafc]">
        <div className="text-center">
          <div className="w-16 h-16 bg-gradient-to-br from-orange-500 to-amber-500 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-xl shadow-blue-500/20 animate-pulse">
            <span className="text-3xl text-white font-bold">P</span>
          </div>
          <div className="text-slate-700 text-lg font-medium">Loading...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col items-center justify-center relative overflow-hidden bg-[#f8fafc]">

      {/* Ambient tonal glows */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        <div className="absolute top-1/4 left-1/4 w-[600px] h-[400px] opacity-50"
             style={{ background: 'radial-gradient(ellipse, rgba(59,130,246,0.18) 0%, transparent 70%)', filter: 'blur(80px)' }} />
        <div className="absolute bottom-1/4 right-1/4 w-[600px] h-[400px] opacity-50"
             style={{ background: 'radial-gradient(ellipse, rgba(16,185,129,0.15) 0%, transparent 70%)', filter: 'blur(80px)' }} />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[600px] opacity-40"
             style={{ background: 'radial-gradient(ellipse, rgba(139,92,246,0.10) 0%, transparent 70%)', filter: 'blur(100px)' }} />
      </div>

      {/* Subtle grid overlay */}
      <div className="absolute inset-0 opacity-[0.04] pointer-events-none"
           style={{ backgroundImage: 'linear-gradient(rgba(15,23,42,0.5) 1px, transparent 1px), linear-gradient(90deg, rgba(15,23,42,0.5) 1px, transparent 1px)', backgroundSize: '60px 60px' }} />

      <div className="relative z-10 w-full max-w-7xl px-6 py-8">

        {/* Header: brand + greeting + actions */}
        <header className="flex items-center justify-between gap-4 mb-8">
          <div className="flex items-center gap-3 min-w-0">
            {user?.organization_logo_url ? (
              <img src={user.organization_logo_url} alt="" className="h-10 w-10 object-contain rounded-xl" />
            ) : (
              <div className="h-10 w-10 rounded-xl bg-gradient-to-br from-orange-500 to-amber-500 flex items-center justify-center shadow-lg shadow-blue-500/20 flex-shrink-0">
                <span className="text-white font-bold">P</span>
              </div>
            )}
            <div className="min-w-0">
              <p className="text-slate-400 text-xs">Welcome back</p>
              <p className="text-slate-800 font-semibold truncate">{user?.name}</p>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            {(user?.user_type === 'platform_superuser' || user?.role === 'superadmin') && (
              <button
                onClick={() => navigate('/platform-admin')}
                className="px-4 py-2 rounded-lg bg-gradient-to-br from-orange-500 to-amber-500 text-white text-sm font-medium shadow-md shadow-blue-500/20 hover:shadow-blue-500/40 transition-shadow"
              >
                Manage Platform
              </button>
            )}
            <button
              onClick={handleLogout}
              className="px-4 py-2 rounded-lg text-slate-500 hover:text-slate-800 hover:bg-slate-100 text-sm transition-colors"
            >
              Sign out
            </button>
          </div>
        </header>

        {/* Title */}
        <div className="mb-6">
          <h1 className="text-[24px] leading-tight font-bold text-slate-800 tracking-tight">Select a platform</h1>
          <p className="text-slate-500 text-sm mt-1">Choose the workspace you want to open.</p>
        </div>

        {/* Platform cards — balanced 3-up grid keeps the page compact, not a tall single column */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 items-stretch">
          {PLATFORMS.map(p => (
            <button
              key={p.id}
              onClick={() => handleSelect(p.id)}
              className="group relative text-left focus:outline-none rounded-2xl"
            >
              <div className={`relative h-full bg-white border border-slate-200/80 rounded-2xl p-5 flex flex-col overflow-hidden
                shadow-sm shadow-slate-200/50 transition-all duration-300
                hover:-translate-y-1 ${p.hoverBorder} ${p.hoverShadow}
                group-focus-visible:ring-2 ${p.ring}`}>

                {/* Hover gradient tint */}
                <div className={`absolute inset-0 bg-gradient-to-br ${p.tint} opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none`} />

                <div className="relative z-10 flex flex-col h-full">
                  {/* Icon */}
                  <div className={`w-10 h-10 bg-gradient-to-br ${p.iconGrad} rounded-xl flex items-center justify-center mb-3 shadow-lg ${p.iconShadow} transition-shadow`}>
                    <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d={p.iconPath} />
                    </svg>
                  </div>

                  {/* Title + full name */}
                  <h2 className="text-lg font-bold text-slate-800 leading-none">{p.code}</h2>
                  <p className={`text-[11px] font-semibold tracking-wide uppercase mt-1 mb-2 ${p.subtitle}`}>{p.name}</p>

                  {/* Description */}
                  <p className="text-slate-600 text-[13px] leading-snug mb-3">{p.desc}</p>

                  {/* Feature pills */}
                  <div className="flex flex-wrap gap-1.5 mb-4">
                    {p.pills.map(f => (
                      <span key={f} className={`px-2.5 py-1 text-[11px] font-medium rounded-lg border ${p.pill}`}>
                        {f}
                      </span>
                    ))}
                  </div>

                  {/* Open affordance — pinned to the bottom so every card is equal height */}
                  <div className={`mt-auto flex items-center gap-1.5 text-[12px] font-semibold ${p.arrow} opacity-0 -translate-x-1 group-hover:opacity-100 group-hover:translate-x-0 transition-all duration-300`}>
                    Open
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                    </svg>
                  </div>
                </div>
              </div>
            </button>
          ))}
        </div>

        {/* Footer */}
        <div className="mt-10">
          <p className="text-slate-400 text-[11px]">
            {user?.organization_name || 'Precision Compliance Group'} &middot; {new Date().getFullYear()}
          </p>
        </div>
      </div>
    </div>
  );
}
